import { css, html, LitElement } from "lit";

import L from "leaflet";

class MapSelectorComponent extends LitElement {
  static get styles() {
    return [
      css`
        :host {
          margin-top: 5px;
          display: block;
          height: calc(100vh - 75px);
          background-color: #666;
          position: relative;
        }
        .controls {
          position: absolute;
          right: 10px;
          top: 10px;
          z-index: 999;
          background: rgba(255, 255, 255, 0.8);
          padding: 10px;
          border-radius: 5px;
          font-size: 14px;
        }
        @media (min-width: 768px) {
            :host {
                margin-top: 25px;
                height: calc(100vh - 95px);
            }
        }
      `,
    ];
  }

  constructor() {
    super();
    this.map = null;
    this.lat = 0;
    this.lon = 0;
    this.box = null;
    this.boxSize = 5; // km
  }

  saveMapLocation() {
    if (!this.map) return;
    const center = this.map.getCenter();
    const zoom = this.map.getZoom();
    localStorage.setItem("mapCenter", JSON.stringify(center));
    localStorage.setItem("mapZoom", zoom.toString());
  }

  loadMapLocation() {
    const center = JSON.parse(localStorage.getItem("mapCenter"));
    const zoom = parseInt(localStorage.getItem("mapZoom"), 10);
    if (center && zoom) {
      this.map.setView(center, zoom);
    }
  }

  firstUpdated() {
    /** @type {HTMLElement} */
    const mapEl = this.shadowRoot.querySelector("#mapid");
    this.map = L.map(mapEl).setView([51.505, -0.09], 3);

    const satelliteLayer = L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}");
    this.map.addLayer(satelliteLayer);

    const lightLayer = L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png");
    this.map.addLayer(lightLayer);

    // When zoomed out, show light map tiles, when zoomed in show satalite map tiles
    this.map.on("zoomend", () => {
      const zoom = this.map.getZoom();
      if (zoom < 10) {
        lightLayer.setOpacity(1);
        satelliteLayer.setOpacity(0);
      } else {
        lightLayer.setOpacity(0);
        satelliteLayer.setOpacity(1);
      }

    });

    this.loadMapLocation();

    // Save map location on move end
    this.map.on("moveend", () => {
      this.saveMapLocation();
    });

    this.map.on("click", (e) => {
      this.lat = e.latlng.lat;
      this.lon = e.latlng.lng;
      this.renderBox();
      this.requestUpdate();
    });
  }

  renderBox() {
    if (!this.map) return;

    const lat = this.lat;
    const lon = this.lon;

    // Bound size is box size in km converted to degrees (approx)
    const boundSize = this.boxSize / 2 / 111; // 1 degree is approx 111 km
    const bounds = L.latLngBounds(
      [lat - boundSize, lon - boundSize],
      [lat + boundSize, lon + boundSize]
    );
    if (this.box) {
      this.map.removeLayer(this.box);
    }
    this.box = L.rectangle(bounds, { color: "#ff7800", weight: 1 }).addTo(this.map);
    this.shadowRoot.querySelector("#btnExport").textContent = "Export";
  }

  exportToClipboard() {
    const data = `${this.lat},${this.lon},${this.boxSize}`;
    navigator.clipboard.writeText(data);
    this.shadowRoot.querySelector("#btnExport").textContent = "Copied!";
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  render() {
    return html`
      <link
        rel="stylesheet"
        href="https://cdn.skypack.dev/leaflet/dist/leaflet.css"
      />
      <div class="controls">
        <input type="text" id="lat" placeholder="Latitude" .value="${this.lat}" readonly />
        <input type="text" id="lon" placeholder="Longitude" .value="${this.lon}" readonly />
        <label for="size">Box Size (km):</label>
        <input type="number" id="size" placeholder="Box Size (km)" .value="${this.boxSize}" @input="${(/**@type {any} */ e) => {
        this.boxSize = e.target.value;
        this.renderBox();
      }}" />
        <button id="btnExport" @click="${this.exportToClipboard}">Export</button>
      </div>
      <div id="mapid" style="height: 100%"></div>
    `;
  }
}

customElements.define("map-selector-component", MapSelectorComponent);
